import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import config from "../../config/config";
import { getAuthToken } from "../../utils/token";
import ProductionPlanReport from "./ProductionPlanReport";
import MaterialUsed from "./MaterialUsed";
import DailyProductionReport from "./DailyProductionReport";

import LatestDispatchPlan from "./LatestDispatchPlan";
import DispatchPlans from "./DispatchPlans";

import ActivityPlanShiftReport from "./ActivityPlanShiftReport";
import PlanActualReport from "../management/Planshift/PlanActualReport";
import ProductionDispatchPlan from "../seniors/ProductionDispatchPlan";
import ProductionDispatchPlanItemBom from "../seniors/ProductionDispatchPlanItemBom";
import ProductionPlanHours from "../seniors/ProductionPlanHours";
export default function ProductionReports() {
  // =========================
  // State
  // =========================

  const [barChartData, setBarChartData] = useState({});
  const [horizontalChart, setHorizontalChart] = useState([]);
  const [itemwiseProduction, setItemwiseProduction] = useState([]);
  const [downtimeChart, setDowntimeChart] = useState([]);

  const [stockAnalysis, setStockAnalysis] = useState([]);
  const [materialStockAnalysis, setMaterialStockAnalysis] = useState([]);

  const [productionLogs, setProductionLogs] = useState([]);
  const [operators, setOperators] = useState([]);
  const [pagination, setPagination] = useState({});

  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");

  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  const [perPage, setPerPage] = useState(10);

  const [filters, setFilters] = useState({
    from_date: "",
    to_date: "",
    operator_id: "",
  });

  // =========================
  // Common API Helper
  // =========================

  const apiRequest = async (method, endpoint, options = {}) => {
    const token = getAuthToken();

    if (!token) return null;

    try {
      const response = await axios({
        method,
        url: `${config.API}${endpoint}`,
        ...options,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...(options.headers || {}),
        },
      });

      return response?.data;
    } catch (error) {
      console.error(
        `API Error [${method} ${endpoint}]:`,
        error?.response?.data || error?.message
      );

      return null;
    }
  };

  // =========================
  // Fetch Production Log
  // =========================

  const fetchProductionLog = async (page = 1) => {
    const result = await apiRequest("GET", "/senmanager/production_log", {
      params: {
        ...filters,
        page,
        per_page: perPage,
      },
    });

    if (result?.success) {
      setProductionLogs(result?.data?.data || []);
      setPagination(result?.data || {});
    } else {
      setProductionLogs([]);
      setPagination({});
    }
  };

  // =========================
  // Fetch Operators
  // =========================

  const fetchOperators = async () => {
    const result = await apiRequest(
      "GET",
      "/senmanager/operators"
    );

    setOperators(
      result?.success
        ? result?.data || []
        : []
    );
  };

  // =========================
  // Fetch Monthly Production
  // =========================

  const fetchBarChartReport = async () => {
    const result = await apiRequest(
      "POST",
      "/senmanager/monthlyproduction",
      {
        data: {
          product_id: selectedProduct || 0,
          start_date: startDate,
          end_date: endDate,
        },
      }
    );

    if (result?.success) {
      setBarChartData(result?.data || {});
    } else {
      setBarChartData({});
    }
  };

  // =========================
  // Fetch Current Stock
  // =========================

  const fetchCurrentStock = async () => {
    const result = await apiRequest(
      "GET",
      "/senmanager/currentstock"
    );

    setStockAnalysis(
      result?.success
        ? result?.data || []
        : []
    );
  };

  // =========================
  // Fetch Material Stock
  // =========================

  const fetchMaterialStock = async () => {
    const result = await apiRequest(
      "GET",
      "/senmanager/materialstock",
      {
        params: {
          month,
          year,
        },
      }
    );

    setMaterialStockAnalysis(
      result?.success
        ? result?.data || []
        : []
    );
  };

  // =========================
  // Fetch Downtime
  // =========================

  const fetchDowntimeChart = async () => {
    const result = await apiRequest(
      "GET",
      "/senmanager/downtime_analysis"
    );

    setDowntimeChart(
      result?.success
        ? result?.data || []
        : []
    );
  };

  // =========================
  // Fetch Itemwise Production
  // =========================

  const fetchItemwiseProduction = async () => {
    const result = await apiRequest(
      "GET",
      "/senmanager/itemwise_production"
    );

    setItemwiseProduction(
      result?.success
        ? result?.data || []
        : []
    );
  };

  // =========================
  // Fetch Machine Utilization
  // =========================

  const fetchHorizontalChart = async () => {
    const result = await apiRequest(
      "GET",
      "/senmanager/machine_utilization"
    );

    setHorizontalChart(
      result?.success
        ? result?.data || []
        : []
    );
  };

  // =========================
  // Load All Reports
  // =========================

  const loadReports = async () => {
    setLoading(true);

    try {
      await Promise.all([
        fetchProductionLog(),
        fetchOperators(),
        fetchBarChartReport(),
        fetchDowntimeChart(),
        fetchItemwiseProduction(),
        fetchHorizontalChart(),
        fetchCurrentStock(),
        fetchMaterialStock(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Filters
  // =========================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const searchData = (page = 1) => {
    fetchProductionLog(page);
  };

  // =========================
  // Excel Export
  // =========================

  const formatDateTime = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const exportExcel = () => {
    if (!productionLogs?.length) return;

    const excelData = productionLogs.map((item, index) => ({
      "S.No": index + 1,
      Date: item.date,
      Shift: item.shift,
      Machine: item.machine?.name || "-",
      Product: item.product?.name || "-",
      Die: item.dietable?.name || "-",
      Produced: item.unit_produced || 0,
      Rejected: item.unit_rejected || 0,
      Recycled: item.unit_recycled || 0,
      "Start (Sec)": formatDateTime(item.cycle_start),
      "End (Sec)": formatDateTime(item.cycle_end),
      "Cycle (Sec)": item.cycle_duration || 0,
      Operator: item.user?.name || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Production Log"
    );

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const file = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(
      file,
      `Production_Log_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`
    );
  };

  // =========================
  // Initial / Filter Loading
  // =========================

  useEffect(() => {
    loadReports();
  }, [
    month,
    year,
    selectedProduct,
    startDate,
    endDate,
  ]);

  // =========================
  // Chart Data
  // =========================

  const BarChartData = {
    labels: ["January"],
    datasets: [
      {
        label: "Planned",
        data: [
          barChartData?.data?.total_planned || 0,
        ],
        backgroundColor: "#0a28a3",
      },
      {
        label: "Actual",
        data: [
          barChartData?.data?.total_produced || 0,
        ],
        backgroundColor: "#4ea0f0",
      },
    ],
  };

  const products = barChartData?.product
    ? Object.entries(barChartData.product)
    : [];

  const colors = [
    "#0a28a3",
    "#4ea0f0",
    "#9fc9fa",
    "#cfe4fb",
    "#e8f1fc",
  ];

  const downtimeChartData = {
    labels:
      downtimeChart?.map(
        (item) => item?.maintenance_type
      ) || [],

    datasets: [
      {
        data:
          downtimeChart?.map(
            (item) => item?.total_downtime
          ) || [],

        backgroundColor: colors.slice(
          0,
          downtimeChart?.length || 0
        ),

        borderWidth: 0,
      },
    ],
  };

  const machineUtilization = {
    labels:
      horizontalChart?.map((item) => {
        const name = item?.machine?.name || "";
        const code =
          item?.machine?.machine_code || "";

        return code
          ? [name, code]
          : [name];
      }) || [],

    datasets: [
      {
        label: "Completion",

        data:
          horizontalChart?.map((item) =>
            Number(
              (
                Number(item?.avg_utilization) *
                100
              ).toFixed(2)
            )
          ) || [],

        backgroundColor: "#0a28a3",
        borderRadius: 4,
        barThickness: 20,
      },
    ],
  };

  // =========================
  // Render
  // =========================

  return (
    <div className="mt-1 container-fluid">
      <ProductionPlanHours />
       <ProductionDispatchPlan />
      <ProductionDispatchPlanItemBom />
      <PlanActualReport />
      <LatestDispatchPlan />
      <ProductionPlanReport />
      <MaterialUsed />
      <DailyProductionReport />
      <DispatchPlans />
      {/* <ProductionPlanActualReport /> */}
    </div>
  );
}